import type { SpaceWire } from 'types/space';
import type { SpaceAction } from '../composition-host/types';
import { resolveWireTargets } from '../composition-host/spaceBus';

/**
 * One row in Try mode's sandboxed action log (SC5): `fired` for a benign action, `blocked` for a
 * write-shaped one (`isWriteShapedAction`), `delivered` for each wire the action routed across.
 * A single fired action can produce a `fired`/`blocked` entry AND zero or more `delivered`
 * entries — wiring and write-shape are orthogonal (a domain adapter's action can still be wired).
 */
export interface ComposerActionLogEntry {
  id: string;
  kind: 'fired' | 'delivered' | 'blocked';
  actionName: string;
  sourceComponentId: string;
  targetComponentId?: string;
  wireId?: string;
  timestamp: number;
}

let nextEntrySeq = 0;

/** Reset the id sequence — test-only, so assertions can predict generated ids. */
export function resetActionLogSequence(): void {
  nextEntrySeq = 0;
}

/**
 * Pure: derives the log rows a single fired `SpaceAction` produces. The fired/blocked entry
 * always comes first (matching the order the action actually happened relative to its
 * deliveries), followed by one `delivered` entry per matching wire (SC5, SC6).
 */
export function deriveActionLogEntries(
  action: SpaceAction,
  wires: SpaceWire[] | undefined,
  writeShaped: boolean,
  timestamp: number
): ComposerActionLogEntry[] {
  const entries: ComposerActionLogEntry[] = [
    {
      id: `log-${timestamp}-${nextEntrySeq++}`,
      kind: writeShaped ? 'blocked' : 'fired',
      actionName: action.name,
      sourceComponentId: action.componentId,
      timestamp,
    },
  ];

  resolveWireTargets(wires, action).forEach((delivery) => {
    entries.push({
      id: `log-${timestamp}-${nextEntrySeq++}`,
      kind: 'delivered',
      actionName: action.name,
      sourceComponentId: action.componentId,
      targetComponentId: delivery.to,
      wireId: delivery.wireId,
      timestamp,
    });
  });

  return entries;
}
